import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Typography,
  IconButton,
  Paper
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  LiveHelp,
  OndemandVideo
} from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    maxWidth: "780px !important",
    width: "100%",
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.25)" : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 14,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8)",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    padding: theme.spacing(2, 3),
  },
  titleText: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  formRoot: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
    overflow: "hidden",
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  sectionCard: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" ? "#111418" : "#F8FAFC",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(0, 0, 0, 0.06)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    gap: theme.spacing(1.5),
  },
  actionButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      color: "rgba(255, 255, 255, 0.3) !important",
      boxShadow: "none",
    },
  },
  outlineButton: {
    borderColor: `${theme.palette.primary.main}80`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 20px",
    fontSize: "0.88rem",
    transition: "all 0.25s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}14`,
      boxShadow: "0 0 10px rgba(0, 229, 255, 0.2)",
    },
  },
  deleteButton: {
    borderColor: "#ff5252",
    color: "#ff5252",
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 16px",
    fontSize: "0.88rem",
    marginRight: "auto",
    "&:hover": {
      borderColor: "#ff1744",
      backgroundColor: "rgba(255, 82, 82, 0.08)",
      boxShadow: "0 0 10px rgba(255, 82, 82, 0.2)",
    },
  },
}));

export default function HelpModal({
  open,
  onClose,
  initialValue,
  onSubmit,
  onDelete,
  loading
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [record, setRecord] = useState({
    title: "",
    video: "",
    description: ""
  });

  useEffect(() => {
    if (!initialValue) return;
    setRecord({
      title: initialValue.title || "",
      video: initialValue.video || "",
      description: initialValue.description || "",
      id: initialValue.id
    });
  }, [initialValue]);

  const isEditing = Boolean(record && record.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <LiveHelp style={{ color: theme.palette.primary.main, fontSize: "1.4rem" }} />
          {isEditing
            ? i18n.t("helps.modal.titleEdit", { title: record.title })
            : i18n.t("helps.modal.titleNew")}
        </Typography>
        <IconButton size="small" onClick={onClose} style={{ color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({ values, errors, touched }) => (
          <Form className={classes.formRoot}>
            <DialogContent className={classes.dialogContent} dividers>
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <OndemandVideo className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("helps.sections.videoInfo")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  {/* TÍTULO */}
                  <Grid item xs={12} sm={7}>
                    <Field
                      as={TextField}
                      label={i18n.t("helps.grid.title")}
                      name="title"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>

                  {/* CÓDIGO DE VIDEO */}
                  <Grid item xs={12} sm={5}>
                    <Field
                      as={TextField}
                      label={i18n.t("helps.grid.video")}
                      name="video"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      helperText="Ej: dQw4w9WgXcQ (ID YouTube / Vimeo)"
                    />
                  </Grid>

                  {/* DESCRIPCIÓN */}
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("helps.grid.description")}
                      name="description"
                      variant="outlined"
                      size="small"
                      multiline
                      rows={4}
                      fullWidth
                      placeholder="Explica de forma concisa qué aprenderá el usuario en este video tutorial..."
                    />
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              {isEditing && (
                <ButtonWithSpinner
                  className={classes.deleteButton}
                  variant="outlined"
                  onClick={() => onDelete(record)}
                  loading={loading}
                >
                  {i18n.t("helps.buttons.delete")}
                </ButtonWithSpinner>
              )}
              <ButtonWithSpinner
                className={classes.outlineButton}
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                {i18n.t("helps.buttons.cancel")}
              </ButtonWithSpinner>
              <ButtonWithSpinner
                className={classes.actionButton}
                type="submit"
                loading={loading}
              >
                {i18n.t("helps.buttons.save")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
